import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Input, InputGroup, Button, ButtonGroup } from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import CustomDataTable from "../../Components/CustomTable/CustomTable";
import KOTLogsServices from "../../services/OrderingServices/KOTLogs";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 600,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const conditionalRowStyles = [
  {
    when: (row) => row.isSuccess === true,
    style: {
      backgroundColor: "#e6f7ec",
      color: "#155724",
    },
  },
  {
    when: (row) => row.isSuccess === false,
    style: {
      backgroundColor: "#fdecea",
      color: "#721c24",
    },
  },
];

const KOTRequestLogs = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");

  const [mode, setMode] = useState("normal");
  const intervalRef = useRef(null);

  const fetchLogs = async (silent = false) => {
    if (!silent) setLoading(true);

    try {
      const res = await KOTLogsServices.getList({
        page,
        pageSize,
        searchText,
      });

      setRows(res.data || []);
      setTotalRows(res.totalCount || 0);
    } catch (error) {
      toast.error("Failed to fetch KOT logs");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const clearLogs = async () => {
    try {
      await KOTLogsServices.deleteLog();
      toast.success("KOT logs cleared successfully");
      fetchLogs();
    } catch (error) {
      toast.error("Failed to clear KOT logs");
    }
  };

  useEffect(() => {
    if (mode === "normal") {
      fetchLogs();
    }
  }, [page, pageSize, searchText, mode]);

  useEffect(() => {
    if (mode === "live") {
      fetchLogs();

      intervalRef.current = setInterval(() => {
        fetchLogs(true);
      }, 7000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [mode, page, pageSize, searchText]);

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (page - 1) * pageSize + index + 1,
      width: "80px",
    },
    {
      name: "Org Code",
      selector: (row) => row.orgCode,
      sortable: true,
      width: "180px",
    },
    {
      name: "Request Time",
      selector: (row) => new Date(row.requestTime).toLocaleString(),
      sortable: true,
      width: "180px",
    },
    {
      name: "Duration (ms)",
      selector: (row) => row.durationMs,
      right: true,
      width: "160px",
    },
    {
      name: "Status",
      selector: (row) => (
        <span
          className={
            row.isSuccess
              ? "text-success fw-semibold"
              : "text-danger fw-semibold"
          }
        >
          {row.isSuccess ? "Success" : "Failed"}
        </span>
      ),
      width: "140px",
    },
    {
      name: "Error",
      selector: (row) => row.errorMessage || "-",
      wrap: true,
    },
  ];

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="KOT Request Logs" pageTitle="Logs" />

        <Row className="mb-3 align-items-center">
          <Col md={6}>
            <InputGroup>
              <Input
                placeholder="Search by org, path, method..."
                value={searchText}
                onChange={(e) => {
                  setPage(1);
                  setSearchText(e.target.value);
                }}
              />
            </InputGroup>
          </Col>

          <Col md={6} className="text-end">
            <ButtonGroup>
              <Button
                color={mode === "normal" ? "primary" : "secondary"}
                onClick={() => setMode("normal")}
              >
                Normal
              </Button>
              <Button
                color={mode === "live" ? "danger" : "secondary"}
                onClick={() => setMode("live")}
              >
                Live
              </Button>
              <Button color="warning" onClick={clearLogs}>
                Clear Logs
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        <CustomDataTable
          columns={columns}
          data={rows}
          conditionalRowStyles={conditionalRowStyles}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={pageSize}
          onChangePage={setPage}
          onChangeRowsPerPage={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          progressPending={loading}
          customStyles={customStyles}
          fixedHeader
          persistTableHead
          progressComponent={
            <div className="my-3 text-center">
              <Triangle height="80" width="80" color="#5B71B9" />
              <h6 className="mt-2">Loading logs...</h6>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default KOTRequestLogs;
